import type { LogsLogsReply } from '@customerio/hydra-ui-api';

/**
 * Activity Logs data layer — types + query keys.
 *
 * Logs are a high-volume paginated feed, so per `services/db/ARCHITECTURE.md` they are NOT
 * normalized into a by-id cache (`useAllCached` / detail slots are for lower-volume entities). This
 * "entity" is therefore a **list + sideload** module rather than a `defineEntity` cluster: a
 * workspace-scoped list key, the wire types (derived from the generated `logs` reply so a spec
 * change surfaces as a compile error here), and the `ActivityLogRow` view-model that joins a log to
 * its sideloaded relations (resolved by id per page).
 *
 * Sideloaded customers/segments are NOT fanned into the shared `customer`/`segment` entity caches
 * (cluster-sideload propagation): that would add a new writer to caches other features read, so it
 * needs its own reviewed change.
 */

/** The two activity-log tabs (the route's `:tab` segment). */
export type ActivityLogTab = 'identified' | 'anonymous';

export type ActivityLog = NonNullable<LogsLogsReply['logs']>[number];
export type ActivityLogCustomer = NonNullable<LogsLogsReply['customers']>[number];
export type ActivityLogObject = NonNullable<LogsLogsReply['objects']>[number];
export type ActivityLogSegment = NonNullable<LogsLogsReply['segments']>[number];
export type ActivityLogSource = NonNullable<ActivityLog['sources']>[number];

/** URL-backed filters. Empty string = unset. */
export interface ActivityLogFilters {
  type: string;
  name: string;
  /** RFC3339 timestamp or '' (date-range filter, gated by the `activity-logs-time-range` flag). */
  from: string;
  to: string;
}

/**
 * A log joined with its sideloaded relations. The related records arrive in the same payload and
 * are resolved by id into this view-model so the cells stay pure (no per-row fetching).
 */
export interface ActivityLogRow {
  log: ActivityLog;
  customer: ActivityLogCustomer | undefined;
  object: ActivityLogObject | undefined;
  /** Resolved from the sideloaded `segments` for segment-build / membership-change logs. */
  segment: ActivityLogSegment | undefined;
}

/** Params that identify a logs list query (a tab + its URL filters). */
export interface ActivityLogListParams {
  tab: ActivityLogTab;
  filters: ActivityLogFilters;
  /** Part of the key: a panel asking for five must not read the feed's cached fifty. */
  limit: number;
  /** One object's own feed (its internal id), rather than the whole workspace's. */
  cioObjectId?: string;
  /** One person's own feed (their internal `cio_id`), rather than the whole workspace's. */
  internalId?: string;
}

/**
 * What a searchable name filter searches: `event` hits event_names, the rest hit attributes —
 * people attributes, one object type's attributes, or that type's relationship attributes.
 */
export type NameSearchScope =
  | { kind: 'event' }
  | { kind: 'attribute' }
  | { kind: 'object_attribute'; objectTypeId: string }
  | { kind: 'relationship_attribute'; objectTypeId: string };

/**
 * Keys live under the `['workspace', id]` subtree so `clearWorkspaceQueries` evicts the feed on
 * workspace switch — a key outside that subtree would survive it and serve stale rows.
 */
export const activityLogKeys = {
  all: (workspaceId: number) => ['workspace', workspaceId, 'activity-log'] as const,
  list: (workspaceId: number, params: ActivityLogListParams) =>
    ['workspace', workspaceId, 'activity-log', 'list', params] as const,
  /** The background check for entries newer than the list shows. Its own key, so it never changes the list. */
  newCheck: (workspaceId: number, params: ActivityLogListParams) =>
    ['workspace', workspaceId, 'activity-log', 'new-check', params] as const,
  nameSearch: (scope: NameSearchScope, workspaceId: number, search: string) =>
    ['workspace', workspaceId, 'activity-log', 'name-search', scope, search] as const,
};
